/**
 * The bar above a list: search, filters, and the actions that apply to the list
 * as a whole.
 *
 * Search is a `type="search"` inside a `role="search"` landmark with a real
 * label, so it is reachable by landmark navigation and announced as a search
 * rather than as an unlabelled text box. The count sits here rather than in the
 * page header because it changes as you filter, and a number that moves belongs
 * next to the thing that moves it.
 */

import { useId, type ReactNode } from 'react';

import { CloseIcon, SearchIcon } from './Icon';

export function FilterBar({
  search,
  onSearch,
  searchPlaceholder = 'Search…',
  searchLabel = 'Search this list',
  children,
  actions,
  count,
}: {
  search?: string;
  onSearch?: (next: string) => void;
  searchPlaceholder?: string;
  searchLabel?: string;
  /** Selects and toggles that narrow the list. */
  children?: ReactNode;
  /** Actions on the list as a whole, pinned right. */
  actions?: ReactNode;
  /** e.g. "5 of 26" — what the filters are currently showing. */
  count?: ReactNode;
}) {
  const id = useId();
  const searchable = typeof search === 'string' && Boolean(onSearch);

  return (
    <div className="filter-bar">
      {searchable ? (
        <search role="search" className="filter-bar__search">
          <label className="visually-hidden" htmlFor={id}>
            {searchLabel}
          </label>
          <SearchIcon className="filter-bar__search-icon" size={15} />
          <input
            id={id}
            type="search"
            className="input filter-bar__input"
            value={search}
            placeholder={searchPlaceholder}
            onChange={(event) => onSearch?.(event.target.value)}
          />
          {search ? (
            <button
              type="button"
              className="filter-bar__clear"
              onClick={() => onSearch?.('')}
              aria-label="Clear search"
              title="Clear search"
            >
              <CloseIcon size={13} />
            </button>
          ) : null}
        </search>
      ) : null}

      {children ? <div className="filter-bar__filters">{children}</div> : null}

      {count != null ? <span className="filter-bar__count muted">{count}</span> : null}
      {actions ? <div className="filter-bar__actions">{actions}</div> : null}
    </div>
  );
}

/**
 * A compact segmented filter — the "All / Critical / Warning" control at the top
 * of a triage list. Radio semantics, so arrow keys move between options and the
 * selected one is announced as selected.
 */
export function SegmentedFilter<V extends string>({
  legend,
  value,
  options,
  onChange,
}: {
  legend: string;
  value: V;
  options: ReadonlyArray<{ value: V; label: ReactNode; count?: number }>;
  onChange: (next: V) => void;
}) {
  const name = useId();
  return (
    <fieldset className="segmented">
      <legend className="visually-hidden">{legend}</legend>
      {options.map((option) => {
        const id = `${name}-${option.value}`;
        const selected = option.value === value;
        return (
          <div key={option.value} className="segmented__item">
            <input
              type="radio"
              id={id}
              name={name}
              className="segmented__input"
              checked={selected}
              onChange={() => onChange(option.value)}
            />
            <label htmlFor={id} className="segmented__label">
              {option.label}
              {option.count != null ? (
                <span className="segmented__count">{option.count}</span>
              ) : null}
            </label>
          </div>
        );
      })}
    </fieldset>
  );
}
